import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Language = 'ar' | 'en';

interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  ar: {
    // Navigation
    'nav.home': 'الرئيسية',
    'nav.products': 'المنتجات',
    'nav.categories': 'الأقسام',
    'nav.cart': 'السلة',
    'nav.account': 'حسابي',
    'nav.search': 'ابحث عن منتجات...',
    
    // Hero
    'hero.title': 'تسوق بأفضل الأسعار',
    'hero.subtitle': 'اكتشف مجموعتنا المميزة من المنتجات عالية الجودة',
    'hero.cta': 'تسوق الآن',
    'hero.offers': 'عروض خاصة',
    
    // Products
    'products.featured': 'منتجات مميزة',
    'products.new': 'وصل حديثاً',
    'products.bestsellers': 'الأكثر مبيعاً',
    'products.addToCart': 'أضف للسلة',
    'products.buyNow': 'اشتري الآن',
    'products.outOfStock': 'نفذت الكمية',
    'products.inStock': 'متوفر',
    'products.viewers': 'يشاهدون الآن',
    'products.sold': 'تم بيعه',
    
    // Product Details
    'product.description': 'وصف المنتج',
    'product.specifications': 'المواصفات',
    'product.reviews': 'التقييمات',
    'product.quantity': 'الكمية',
    'product.color': 'اللون',
    'product.size': 'المقاس',
    'product.bundle': 'عروض الباقات',
    'product.buyBundle': 'اشتري أكثر ووفر أكثر',
    
    // Cart
    'cart.title': 'سلة التسوق',
    'cart.empty': 'السلة فارغة',
    'cart.subtotal': 'المجموع الفرعي',
    'cart.shipping': 'الشحن',
    'cart.total': 'الإجمالي',
    'cart.checkout': 'إتمام الطلب',
    'cart.continue': 'متابعة التسوق',
    'cart.remove': 'إزالة',
    
    // Checkout
    'checkout.title': 'إتمام الطلب',
    'checkout.contact': 'معلومات التواصل',
    'checkout.shipping': 'عنوان الشحن',
    'checkout.payment': 'طريقة الدفع',
    'checkout.name': 'الاسم الكامل',
    'checkout.phone': 'رقم الهاتف',
    'checkout.altPhone': 'رقم هاتف بديل',
    'checkout.governorate': 'المحافظة',
    'checkout.city': 'المدينة',
    'checkout.address': 'العنوان التفصيلي',
    'checkout.notes': 'ملاحظات إضافية',
    'checkout.cod': 'الدفع عند الاستلام',
    'checkout.card': 'بطاقة ائتمان',
    'checkout.placeOrder': 'تأكيد الطلب',
    'checkout.coupon': 'كود الخصم',
    'checkout.applyCoupon': 'تطبيق',
    
    // Thank You
    'thankyou.title': 'شكراً لك!',
    'thankyou.message': 'تم استلام طلبك بنجاح',
    'thankyou.orderNumber': 'رقم الطلب',
    'thankyou.recommended': 'قد يعجبك أيضاً',
    
    // Footer
    'footer.about': 'من نحن',
    'footer.contact': 'تواصل معنا',
    'footer.privacy': 'سياسة الخصوصية',
    'footer.terms': 'الشروط والأحكام',
    'footer.shipping': 'سياسة الشحن',
    'footer.returns': 'سياسة الاسترجاع',
    'footer.followUs': 'تابعنا',
    'footer.rights': 'جميع الحقوق محفوظة',
    
    // Common
    'common.currency': 'ج.م',
    'common.discount': 'خصم',
    'common.off': 'خصم',
    'common.free': 'مجاني',
    'common.loading': 'جاري التحميل...',
    'common.error': 'حدث خطأ',
    'common.success': 'تم بنجاح',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.delete': 'حذف',
    'common.edit': 'تعديل',
    'common.add': 'إضافة',
    'common.search': 'بحث',
    'common.filter': 'تصفية',
    'common.sort': 'ترتيب',
    'common.all': 'الكل',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.products': 'Products',
    'nav.categories': 'Categories',
    'nav.cart': 'Cart',
    'nav.account': 'Account',
    'nav.search': 'Search products...',
    
    // Hero
    'hero.title': 'Shop at Best Prices',
    'hero.subtitle': 'Discover our premium collection of high-quality products',
    'hero.cta': 'Shop Now',
    'hero.offers': 'Special Offers',
    
    // Products
    'products.featured': 'Featured Products',
    'products.new': 'New Arrivals',
    'products.bestsellers': 'Best Sellers',
    'products.addToCart': 'Add to Cart',
    'products.buyNow': 'Buy Now',
    'products.outOfStock': 'Out of Stock',
    'products.inStock': 'In Stock',
    'products.viewers': 'viewing now',
    'products.sold': 'sold',
    
    // Product Details
    'product.description': 'Description',
    'product.specifications': 'Specifications',
    'product.reviews': 'Reviews',
    'product.quantity': 'Quantity',
    'product.color': 'Color',
    'product.size': 'Size',
    'product.bundle': 'Bundle Offers',
    'product.buyBundle': 'Buy More & Save More',
    
    // Cart
    'cart.title': 'Shopping Cart',
    'cart.empty': 'Your cart is empty',
    'cart.subtotal': 'Subtotal',
    'cart.shipping': 'Shipping',
    'cart.total': 'Total',
    'cart.checkout': 'Checkout',
    'cart.continue': 'Continue Shopping',
    'cart.remove': 'Remove',
    
    // Checkout
    'checkout.title': 'Checkout',
    'checkout.contact': 'Contact Information',
    'checkout.shipping': 'Shipping Address',
    'checkout.payment': 'Payment Method',
    'checkout.name': 'Full Name',
    'checkout.phone': 'Phone Number',
    'checkout.altPhone': 'Alternative Phone',
    'checkout.governorate': 'Governorate',
    'checkout.city': 'City',
    'checkout.address': 'Detailed Address',
    'checkout.notes': 'Additional Notes',
    'checkout.cod': 'Cash on Delivery',
    'checkout.card': 'Credit Card',
    'checkout.placeOrder': 'Place Order',
    'checkout.coupon': 'Coupon Code',
    'checkout.applyCoupon': 'Apply',
    
    // Thank You
    'thankyou.title': 'Thank You!',
    'thankyou.message': 'Your order has been received',
    'thankyou.orderNumber': 'Order Number',
    'thankyou.recommended': 'You May Also Like',
    
    // Footer
    'footer.about': 'About Us',
    'footer.contact': 'Contact Us',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms & Conditions',
    'footer.shipping': 'Shipping Policy',
    'footer.returns': 'Returns Policy',
    'footer.followUs': 'Follow Us',
    'footer.rights': 'All Rights Reserved',
    
    // Common
    'common.currency': 'EGP',
    'common.discount': 'Discount',
    'common.off': 'OFF',
    'common.free': 'Free',
    'common.loading': 'Loading...',
    'common.error': 'Error occurred',
    'common.success': 'Success',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.all': 'All',
  },
};

export const useLanguage = create<LanguageState>()(
  persist(
    (set, get) => ({
      language: 'ar',
      setLanguage: (lang) => {
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
        set({ language: lang });
      },
      t: (key) => {
        const { language } = get();
        return translations[language][key] || key;
      },
    }),
    {
      name: 'language-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          document.documentElement.dir = state.language === 'ar' ? 'rtl' : 'ltr';
          document.documentElement.lang = state.language;
        }
      },
    }
  )
);
